import { useEffect, useMemo, useState } from "react";
import type { RichChallengeProps } from "./richChallenges.ts";
import { TeaserVictory } from "./TeaserVictory.tsx";

// Rich "challenge" renderer for challenge_type === "obd2_scanner".
// Content (DTC list, prompts) is authored server-side and arrives as a JSON
// string in challengePayload — this is how server content threads into a rich
// client mini-app. The player reads the trouble codes, spots the rogue one,
// and submits it; submission goes through the normal completeObjective path
// (validated server-side), so the answer is never trusted to the client.

interface Dtc {
  code: string;
  desc: string;
  rogue?: boolean;
}
interface Payload {
  vin?: string;
  intro?: string;
  codes?: Dtc[];
  prompt?: string;
}

export function Obd2Scanner(props: RichChallengeProps) {
  const { payload, response, onSubmit, onClose } = props;
  const data = useMemo<Payload>(() => {
    try {
      return JSON.parse(payload || "{}");
    } catch {
      return {};
    }
  }, [payload]);

  const codes = data.codes || [];
  // Stream the codes in one at a time for a scan-tool feel.
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    setRevealed(0);
    const t = setInterval(
      () => setRevealed((n) => (n >= codes.length ? n : n + 1)),
      500,
    );
    return () => clearInterval(t);
  }, [payload]); // eslint-disable-line react-hooks/exhaustive-deps

  const [code, setCode] = useState("");
  const won = response.startsWith("Correct");

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(2,4,6,0.86)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "MelGame, 'Courier New', monospace",
      }}
    >
      <div
        style={{
          width: "min(860px, 92vw)",
          height: "min(640px, 88vh)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "#05070a",
          border: "1px solid var(--ro-accent)",
          borderRadius: 8,
          boxShadow: "0 0 30px var(--ro-accent-soft), 0 16px 50px rgba(0,0,0,0.7)",
          color: "var(--ro-text)",
          padding: 18,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            borderBottom: "1px solid var(--ro-border)",
            paddingBottom: 8,
            marginBottom: 10,
          }}
        >
          <span style={{ color: "var(--ro-accent-bright)", letterSpacing: "0.15em", fontSize: 22 }}>
            ▣ OBD-II SCAN TOOL
          </span>
          <span style={{ fontSize: 16, color: "var(--ro-text-dim)" }}>
            VIN {data.vin || "————"}
          </span>
        </div>

        <div style={{ fontSize: 20, color: "var(--ro-text-dim)", marginBottom: 8 }}>
          {data.intro || "Reading stored diagnostic trouble codes..."}
        </div>

        <div
          style={{
            background: "#0a0e13",
            border: "1px solid var(--ro-border)",
            borderRadius: 4,
            padding: 10,
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            marginBottom: 12,
          }}
        >
          {codes.slice(0, revealed).map((d, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 12,
                padding: "3px 0",
                color: d.rogue ? "#ff5a5a" : "var(--ro-text)",
                textShadow: d.rogue ? "0 0 10px rgba(255,60,60,0.6)" : "none",
                fontWeight: d.rogue ? 700 : 400,
              }}
            >
              <span style={{ width: 100, flexShrink: 0, fontSize: 21 }}>{d.code}</span>
              <span style={{ fontSize: 19 }}>{d.desc}</span>
            </div>
          ))}
          {revealed < codes.length && (
            <div style={{ color: "var(--ro-accent)" }}>scanning…▌</div>
          )}
        </div>

        {won ? (
          // Teaser finale won: hand the whole screen to the celebration
          // overlay (fireworks + claim code front and center).
          <TeaserVictory
            response={response.replace(/^Correct answer\.\s*/, "")}
            onClose={onClose}
          />
        ) : (
          <>
            <div style={{ fontSize: 20, marginBottom: 6 }}>
              {data.prompt || "Enter the trouble code to clear:"}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="Input"
                value={code}
                autoFocus
                placeholder="e.g. U0100"
                onChange={(e) => setCode(e.target.value)}
                onKeyDownCapture={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter" && code.trim()) onSubmit(code.trim());
                }}
                style={{ flex: 1, textTransform: "uppercase", fontSize: 22 }}
              />
              <button
                className="Button green"
                onClick={() => code.trim() && onSubmit(code.trim())}
              >
                CLEAR CODE
              </button>
            </div>
            {response && (
              <div
                style={{
                  marginTop: 8,
                  fontSize: 19,
                  fontStyle: "italic",
                  color: "#ff8a8a",
                }}
              >
                {response}
              </div>
            )}
          </>
        )}

        <div style={{ textAlign: "right", marginTop: 12 }}>
          <button className="Button" onClick={onClose} style={{ color: "var(--ro-text-dim)" }}>
            {won ? "Close" : "Disconnect"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Obd2Scanner;
